import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, PlayCircle, ImageOff } from "lucide-react";

/**
 * Swipeable exercise demo carousel. Slides are layered in this order:
 *   1. Primary YouTube embed       (if `youtubeId` is set)
 *   2. Fallback YouTube embed      (if `youtubeId2` is set)
 *   3. Looped GIF-style frame pair (if `frames` is non-empty)
 *   4. Placeholder (when nothing is available)
 *
 * Users swipe / tap-arrow between slides so that if one YouTube video is
 * taken down or geo-blocked they can flip to the next option without
 * leaving the page. Each slide remains a static iframe / img — only the
 * active one is fully visible, but the others stay mounted so swipes feel
 * instant (no iframe reloads).
 */
export default function ExerciseDemo({
    frames = [],
    youtubeId = "",
    youtubeId2 = "",
    name = "",
    className = "",
}) {
    const validFrames = Array.isArray(frames) ? frames.filter(Boolean) : [];
    const [frameIdx, setFrameIdx] = useState(0);
    const [broken, setBroken] = useState(false);

    // Compose ordered slide list — each entry is `{ kind, payload, label }`.
    const slides = [];
    if (youtubeId) slides.push({ kind: "yt", payload: youtubeId, label: "Video · primary" });
    if (youtubeId2) slides.push({ kind: "yt", payload: youtubeId2, label: "Video · alternate" });
    if (!broken && validFrames.length > 0) slides.push({ kind: "frames", payload: validFrames, label: "Looped demo" });

    const [slide, setSlide] = useState(0);
    useEffect(() => {
        // Reset to slide 0 whenever the exercise (and thus slide makeup) changes.
        setSlide(0);
    }, [youtubeId, youtubeId2, validFrames.length]);

    // Cycle the GIF frames only while the GIF slide is the active one.
    useEffect(() => {
        const current = slides[slide];
        if (!current || current.kind !== "frames" || current.payload.length < 2) return;
        const id = setInterval(() => setFrameIdx((i) => (i + 1) % current.payload.length), 600);
        return () => clearInterval(id);
    }, [slide, slides]);

    // Touch swipe — minimal, no library.
    const [touchStartX, setTouchStartX] = useState(null);
    const onTouchStart = (e) => setTouchStartX(e.touches[0]?.clientX ?? null);
    const onTouchEnd = (e) => {
        if (touchStartX == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
        if (Math.abs(dx) > 40) {
            if (dx < 0) goNext();
            else goPrev();
        }
        setTouchStartX(null);
    };
    const goNext = () => setSlide((i) => (i + 1) % Math.max(1, slides.length));
    const goPrev = () => setSlide((i) => (i - 1 + slides.length) % Math.max(1, slides.length));

    // Empty / fully-broken state
    if (slides.length === 0) {
        return (
            <div className={`tactical-card overflow-hidden ${className}`} data-testid="exercise-demo-placeholder">
                <div className="aspect-video bg-gradient-to-br from-[#141414] to-[#2a2a2a] flex flex-col items-center justify-center text-center px-6">
                    {broken ? <ImageOff className="h-12 w-12 text-accent-red" /> : <PlayCircle className="h-12 w-12 text-accent-red" />}
                    <p className="mt-3 label-eyebrow text-muted-foreground">{broken ? "Demo unavailable" : "Form demo coming soon"}</p>
                    <p className="mt-1 max-w-xs text-xs text-muted-foreground">See the setup, execution, and mistakes below — your written cues never sleep.</p>
                </div>
            </div>
        );
    }

    const showSwipeUI = slides.length > 1;
    const current = slides[slide];

    return (
        <div
            className={`tactical-card overflow-hidden ${className}`}
            data-testid="exercise-demo-carousel"
            onTouchStart={showSwipeUI ? onTouchStart : undefined}
            onTouchEnd={showSwipeUI ? onTouchEnd : undefined}
        >
            <div className="relative aspect-video bg-black">
                {slides.map((s, i) => (
                    <div
                        key={`${s.kind}-${i}`}
                        className="absolute inset-0 transition-opacity duration-200"
                        style={{ opacity: i === slide ? 1 : 0, pointerEvents: i === slide ? "auto" : "none" }}
                        aria-hidden={i !== slide}
                        data-testid={`exercise-demo-slide-${i}`}
                    >
                        {s.kind === "yt" ? (
                            <iframe
                                src={`https://www.youtube.com/embed/${s.payload}`}
                                title={`${name} — ${s.label}`}
                                className="h-full w-full"
                                allowFullScreen
                                data-testid={`exercise-demo-yt-${i}`}
                            />
                        ) : (
                            <div className="relative h-full w-full bg-[#0d0d0d]">
                                {s.payload.map((src, j) => (
                                    <img
                                        key={src}
                                        src={src}
                                        alt={`${name} — frame ${j + 1}`}
                                        onError={() => setBroken(true)}
                                        className="absolute inset-0 h-full w-full object-contain transition-opacity duration-200"
                                        style={{ opacity: j === frameIdx ? 1 : 0 }}
                                        data-testid={`exercise-demo-frame-${j}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {/* Slide-count + label badge */}
                <div className="absolute bottom-2 right-2 rounded-sm bg-black/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/80">
                    {current.label}{showSwipeUI ? ` · ${slide + 1}/${slides.length}` : ""}
                </div>

                {showSwipeUI && (
                    <>
                        {/* Arrow controls — always visible for keyboard / desktop users */}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); goPrev(); }}
                            aria-label="Previous demo"
                            data-testid="exercise-demo-prev"
                            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white/85 transition hover:bg-black/80 hover:text-white"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); goNext(); }}
                            aria-label="Next demo"
                            data-testid="exercise-demo-next"
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white/85 transition hover:bg-black/80 hover:text-white"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>

                        {/* Dot indicators */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                            {slides.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setSlide(i); }}
                                    aria-label={`Go to demo ${i + 1}`}
                                    data-testid={`exercise-demo-dot-${i}`}
                                    className={`h-1.5 rounded-full transition-all ${i === slide ? "w-6 bg-accent-red" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {showSwipeUI && (
                <div
                    className="border-t border-border bg-black/40 px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground"
                    data-testid="exercise-demo-swipe-hint"
                >
                    Swipe or tap → to flip between {slides.length} demo{slides.length > 1 ? "s" : ""}
                </div>
            )}
        </div>
    );
}
